import { JSX, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { ADMIN } from 'src/core/constants/roles'
import Logger from 'src/core/utils/Logger'

import { allowed } from './audienceFilter/AudienceHelper'

interface IVisualAudienceProps {
  write?: string[]
  read?: string[]
  component: any
  hiddenComponent?: () => JSX.Element
}

enum UserRights {
  READ = 'read',
  WRITE = 'write',
}

export function VisualAudience<T>(props: T & IVisualAudienceProps) {
  const [currentRights, setCurrentRights] = useState<UserRights[]>()
  const user = useSelector(selectUser)
  const roles = user.roleIds

  useEffect(() => {
    if (!props.read && !props.write) {
      Logger.error('Missing roles for Audience filter.')
      return
    }

    if (!roles || !roles.length) {
      return
    }

    const isUserSuperAdmin = roles.some((role) => role === ADMIN)
    if (isUserSuperAdmin) {
      return setRightsForSuperAdmin()
    }

    const skipReadCheck = props.write && !props.read
    if (skipReadCheck) {
      return setRightsForSkipRead()
    }

    const onlySuperAdminCanEdit = !props.write && props.read
    if (onlySuperAdminCanEdit) {
      return setRightsForOnlySuperAdminCanEdit()
    }

    return setRightsForDefaultCase()
  }, [roles])

  const setRightsForSuperAdmin = () => {
    setCurrentRights([UserRights.READ, UserRights.WRITE])
    return
  }

  const setRightsForSkipRead = () => {
    if (canUserWrite()) {
      setCurrentRights([UserRights.READ, UserRights.WRITE])
    } else {
      setCurrentRights([UserRights.READ])
    }
    return
  }

  const setRightsForOnlySuperAdminCanEdit = () => {
    if (canUserRead()) {
      setCurrentRights([UserRights.READ])
    } else {
      setCurrentRights([])
    }
    return
  }

  const setRightsForDefaultCase = () => {
    if (props.write && props.read) {
      if (canUserRead()) {
        if (canUserWrite()) {
          setCurrentRights([UserRights.READ, UserRights.WRITE])
        } else {
          setCurrentRights([UserRights.READ])
        }
      } else if (canUserWrite()) {
        setCurrentRights([UserRights.WRITE])
      }
      return
    }
  }

  const canUserWrite = (writeArr: string[] | undefined = props.write): boolean =>
    !!(writeArr && roles && allowed(roles, writeArr))

  const canUserRead = (readArr: string[] | undefined = props.read): boolean =>
    !!(readArr && roles && allowed(roles, readArr))

  if (!currentRights || !currentRights.length) {
    return props.hiddenComponent ? props.hiddenComponent() : <></>
  }
  const newProps = Object.assign({}, props, {
    read: undefined,
    write: undefined,
    component: undefined,
  })

  if (currentRights.length === 1) {
    const [currentItem] = currentRights
    if (currentItem === UserRights.WRITE) {
      return <props.component {...newProps} />
    }
    return <props.component {...newProps} disabled />
  }
  if (currentRights.length === 2) {
    return <props.component {...newProps} />
  }

  return props.hiddenComponent ? props.hiddenComponent() : <></>
}

export default VisualAudience
